/**
 * @zendir/ui - SpacecraftCard Component
 *
 * Enterprise-grade spacecraft information card.
 *
 * AstroUXDS Compliance:
 * - Status colors used for health/operational status only
 * - Orbit type uses Zendir-Astro data viz colors (categorical)
 * - Zendir accent colors for interactive elements
 * - Tabular numbers with fontVariantNumeric for data
 * - Sentence-case labels per Tier 3 compliance
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - WCAG 2.1 AA accessibility
 * - Keyboard navigation
 * - Smooth animations with reduced-motion support
 * - Loading skeleton state
 * - Error boundary support
 * - Real SDK integration via optional client prop
 */

import React, { memo, useCallback, useMemo } from "react";
import type { SpacecraftPosition, TelemetryData } from "../types";
import { useTheme } from "../theme";
import { Icon, type IconName } from "../core/display/Icon";
import { CardHeader } from "../core/display/CardHeader";
import { HeaderIconWithStatus } from "../core/display/HeaderIconWithStatus";
import { useCompactMode } from "../hooks";
import { useCardBorderStyle } from "../context/DisplaySettingsContext";

// ZendirClient is optional - only used when SDK is installed
type ZendirClient = any;
import {
  safeNumber,
  formatCoordinate,
  formatAltitude,
  formatVelocity,
  formatPercentage,
  formatTime,
  getStatusColor,
  classNames,
  safeAccentText,
  type StatusLevel,
} from "../utils";

export interface SpacecraftCardProps {
  /** Spacecraft position data (for direct data mode) */
  spacecraft?: SpacecraftPosition;
  /** Optional telemetry data */
  telemetry?: TelemetryData;
  /** Show expanded orbit info */
  showOrbit?: boolean;
  /** Show telemetry summary */
  showTelemetry?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Click handler */
  onClick?: (spacecraft: SpacecraftPosition) => void;
  /** Custom className */
  className?: string;
  /** Connected mode: SDK client */
  client?: ZendirClient;
  /** Connected mode: Spacecraft ID to fetch */
  spacecraftId?: string;
  /** Enable compact mode - shows condensed view that expands on hover/click */
  compact?: boolean;
  /** Start expanded when in compact mode (pinned state) */
  defaultExpanded?: boolean;
  /** Callback when pin state changes in compact mode */
  onPinChange?: (isPinned: boolean) => void;
}

/**
 * Get orbit type color based on orbit classification
 * Uses Zendir-Astro palette for categorical (non-status) classification
 */
function getOrbitTypeColor(
  orbitType: string | undefined,
  accentColor: string,
): string {
  // Orbit type colors using pure Zendir brand palette
  // These are categorical colors, not status colors
  const colors: Record<string, string> = {
    LEO: "#3E3CFF", // Zendir Electric - Low Earth Orbit
    MEO: "#3548C0", // Zendir Prussian Blue Light - Medium Earth Orbit
    GEO: "#9D70FF", // Zendir Purple - Geostationary
    HEO: "#1B2DA0", // Zendir Prussian Blue - Highly Elliptical
    SSO: "#B48DFF", // Zendir Purple Light - Sun-Synchronous
    POLAR: "#6B69FF", // Zendir Electric Light - Polar
  };
  return colors[orbitType?.toUpperCase() ?? ""] ?? accentColor;
}

/**
 * Derive health status from spacecraft or telemetry
 * Prefers telemetry.health.status, falls back to spacecraft.status
 */
function deriveHealthStatus(
  spacecraftStatus: StatusLevel | undefined,
  telemetryStatus: string | undefined,
): StatusLevel {
  // Prefer telemetry if available, but also validate it's a proper StatusLevel
  if (telemetryStatus) {
    const validStatuses: StatusLevel[] = [
      "off",
      "standby",
      "normal",
      "caution",
      "serious",
      "critical",
    ];
    const normalized = telemetryStatus.toLowerCase();
    if (validStatuses.includes(normalized as StatusLevel)) {
      return normalized as StatusLevel;
    }
    // Legacy mapping for backwards compatibility
    switch (normalized) {
      case "healthy":
      case "nominal":
      case "operational":
        return "normal";
      case "warning":
      case "degraded":
        return "caution";
      case "failed":
      case "error":
        return "critical";
      case "idle":
        return "standby";
    }
  }
  // Use spacecraft.status directly (already typed as StatusLevel)
  return spacecraftStatus ?? "off";
}

/**
 * SpacecraftCard - Displays spacecraft position and status information
 *
 * @example
 * ```tsx
 * // Direct data mode
 * <SpacecraftCard
 *   spacecraft={spacecraftData}
 *   telemetry={telemetryData}
 *   showOrbit
 *   showTelemetry
 *   onClick={() => {}}
 * />
 *
 * // Connected mode (auto-fetches via SDK)
 * <SpacecraftCard
 *   client={zendirClient}
 *   spacecraftId="iss-25544"
 *   showTelemetry
 * />
 * ```
 */
export const SpacecraftCard = memo(function SpacecraftCard({
  spacecraft,
  telemetry,
  showOrbit = false,
  showTelemetry = false,
  loading = false,
  onClick,
  className = "",
  compact = false,
  defaultExpanded = false,
  onPinChange,
}: SpacecraftCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";
  const useGlassMode = isBoldVariant || isMinimalVariant;

  // Glass accent colors for bold/minimal themes (matching Container/AstroChart)
  const glassAccentColor = tokens.colors.accent.primary;
  const glassAccentMuted = `${glassAccentColor}66`;

  // WCAG AA safe accent for use as foreground text on dark backgrounds
  const accentTextColor = useMemo(
    () => safeAccentText(tokens.colors.accent.primary),
    [tokens.colors.accent.primary],
  );

  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};
  // Get card border style based on accentBorders setting
  const cardBorderStyle = useCardBorderStyle(tokens, isTransparentTheme);

  // Glass mode container style (matching Container/AstroChart for bold/minimal)
  const glassContainerStyle: React.CSSProperties = useGlassMode
    ? {
        backgroundColor: "rgba(10, 15, 25, 0.35)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${glassAccentMuted}`,
        borderTop: isBoldVariant
          ? `2px solid ${glassAccentColor}`
          : `1px solid ${glassAccentColor}`,
      }
    : {};

  // Compact mode state management
  const {
    expanded,
    isPinned,
    isHovered: _isHovered,
    handlers: compactHandlers,
    transitionDuration: _compactTransition,
  } = useCompactMode({
    compact,
    defaultExpanded,
    onPinChange,
  });

  // Smooth animation timing
  const animationDuration = prefersReducedMotion ? 0 : 250;
  const animationEasing = "cubic-bezier(0.4, 0, 0.2, 1)";

  // Memoized click handler
  const _handleClick = useCallback(() => {
    if (onClick && spacecraft) {
      onClick(spacecraft);
    }
  }, [onClick, spacecraft]);

  // Keyboard handler for accessibility
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && spacecraft && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick(spacecraft);
      }
    },
    [onClick, spacecraft],
  );

  // Memoized computed values
  const orbitColor = useMemo(
    () =>
      getOrbitTypeColor(spacecraft?.orbitType, tokens.colors.accent.primary),
    [spacecraft?.orbitType, tokens.colors.accent.primary],
  );

  const healthStatus = useMemo(
    () => deriveHealthStatus(spacecraft?.status, telemetry?.health?.status),
    [spacecraft?.status, telemetry?.health?.status],
  );

  const statusColor = useMemo(
    () => getStatusColor(healthStatus),
    [healthStatus],
  );

  // Animation duration (respects reduced motion)
  const transitionDuration = prefersReducedMotion ? "0ms" : "200ms";

  // Combined click handler for compact mode and onClick (must be before early returns)
  const combinedClickHandler = useCallback(() => {
    if (compact) {
      compactHandlers.onClick();
    }
    if (onClick && spacecraft) {
      onClick(spacecraft);
    }
  }, [compact, compactHandlers, onClick, spacecraft]);

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames("zendir-spacecraft-card", "loading", className)}
        role="article"
        aria-busy="true"
        aria-label="Loading spacecraft data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...cardBorderStyle,
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 180,
        }}
      >
        {/* Loading skeleton */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.1)",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: 16,
                width: "60%",
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: 4,
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 12,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <div key={i}>
              <div
                style={{
                  height: 10,
                  width: "40%",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: 2,
                  marginBottom: 4,
                }}
              />
              <div
                style={{
                  height: 14,
                  width: "70%",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: 2,
                }}
              />
            </div>
          ))}
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // Empty state
  if (!spacecraft) {
    return (
      <div
        className={classNames("zendir-spacecraft-card", "empty", className)}
        role="article"
        aria-label="No spacecraft data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...(tokens.colors.border.cardStyleDashed ?? {}),
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{ marginBottom: 8, opacity: 0.5 }}
        >
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
        <p style={{ margin: 0, fontSize: tokens.typography.body[2].fontSize }}>
          No spacecraft data available
        </p>
      </div>
    );
  }

  // Compact view - shows key info in a single row
  // Compact metric item component for consistent styling
  const CompactMetric = ({
    icon,
    label,
    value,
    color,
  }: {
    icon: IconName;
    label: string;
    value: string | number;
    color?: string;
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        padding: `2px 0`,
      }}
    >
      <Icon
        name={icon}
        size={12}
        color={color ?? tokens.colors.text.secondary}
      />
      <span
        style={{
          fontSize: tokens.typography.fontSize.micro, // 0.5625rem / 9px (AstroUXDS micro)
          fontWeight: tokens.typography.fontWeight.medium,
          fontFamily: tokens.typography.fontFamily.primary, // Public Sans → Roboto fallback → system stack
          color: tokens.colors.text.tertiary,
          textTransform: "uppercase",
          minWidth: 8,
        }}
      >
        {label.charAt(0)}
      </span>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          fontFamily: tokens.typography.fontFamily.mono,
          fontSize: tokens.typography.fontSize.xs,
          color: color ?? tokens.colors.text.primary,
          fontWeight: tokens.typography.fontWeight.medium,
        }}
      >
        {value}
      </span>
    </div>
  );

  // Compact view content - reusable for rendering
  const renderCompactContent = () => {
    const power = telemetry?.subsystems?.power ?? telemetry?.power;
    const batteryLevel = power?.batteryLevel;
    const batteryStatusLocal =
      batteryLevel !== undefined
        ? batteryLevel > 50
          ? "normal"
          : batteryLevel > 20
            ? "caution"
            : "critical"
        : "off";

    // Status color for header icon
    const statusColor = getStatusColor(healthStatus);

    return (
      <>
        {/* Header: Icon (status colored) → Status symbol → Name → Badges */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: tokens.spacing.xs,
            paddingBottom: tokens.spacing.xs,
            borderBottom: useGlassMode
              ? "none"
              : `1px solid ${tokens.colors.border.muted}`,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Left side: Icon + Name + Accent line */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.xs,
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Icon with status badge - MonitoringIcon pattern */}
            <HeaderIconWithStatus
              icon="satellite-transmit"
              size={18}
              status={healthStatus}
              statusColor={statusColor}
            />
            {/* Name - accent color for glass mode */}
            <span
              style={{
                fontWeight: tokens.typography.fontWeight.semibold,
                fontSize: tokens.typography.fontSize.sm,
                fontFamily: tokens.typography.fontFamily.primary,
                color: useGlassMode ? glassAccentColor : undefined,
                textShadow: useGlassMode
                  ? `0 0 16px ${glassAccentMuted}`
                  : undefined,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {(spacecraft.name ?? spacecraft.id ?? "Unknown").split(" ")[0]}
            </span>
            {/* Accent line for glass mode */}
            {useGlassMode && (
              <div
                style={{
                  flex: 1,
                  height: isBoldVariant ? "2px" : "1px",
                  minWidth: 20,
                  background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 60%, transparent 100%)`,
                  boxShadow: isBoldVariant
                    ? `0 0 8px ${glassAccentMuted}`
                    : undefined,
                }}
              />
            )}
          </div>

          {/* Right side: Orbit type badge + Pin */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.xs,
              flexShrink: 0,
            }}
          >
            {/* Orbit type badge */}
            <span
              style={{
                backgroundColor: `${orbitColor}20`,
                color: orbitColor,
                padding: `1px 6px`,
                borderRadius: tokens.borderRadius.sm,
                fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
                fontWeight: tokens.typography.fontWeight.medium,
                fontFamily: tokens.typography.fontFamily.primary,
              }}
            >
              {spacecraft.orbitType ?? "—"}
            </span>
            {/* Pin badge */}
            {isPinned && (
              <Icon
                name="lock"
                size={10}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>
        </div>

        {/* Metrics grid - 3 columns */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
          }}
        >
          {/* Position metrics */}
          <CompactMetric
            icon="location"
            label="Latitude"
            value={formatCoordinate(spacecraft.latitude, "lat")}
          />
          <CompactMetric
            icon="location"
            label="Longitude"
            value={formatCoordinate(spacecraft.longitude, "lon")}
          />
          <CompactMetric
            icon="orbit"
            label="Altitude"
            value={formatAltitude(spacecraft.altitude)}
            color={orbitColor}
          />

          {/* Velocity */}
          {spacecraft.velocity !== undefined && (
            <CompactMetric
              icon="rocket"
              label="Velocity"
              value={formatVelocity(spacecraft.velocity)}
            />
          )}

          {/* Telemetry data if available */}
          {batteryLevel !== undefined && (
            <CompactMetric
              icon="battery"
              label="Battery"
              value={formatPercentage(batteryLevel)}
              color={getStatusColor(batteryStatusLocal as StatusLevel)}
            />
          )}

          {/* Inclination if available */}
          {spacecraft.inclination !== undefined && (
            <CompactMetric
              icon="orbit"
              label="Inclination"
              value={`${safeNumber(spacecraft.inclination, 1)}°`}
            />
          )}
        </div>
      </>
    );
  };

  // Compact mode with in-place expansion - cards push each other
  if (compact) {
    return (
      <article
        className={classNames(
          "zendir-spacecraft-card",
          "compact",
          expanded ? "expanded" : "",
          className,
        )}
        {...compactHandlers}
        aria-label={`Spacecraft: ${spacecraft.name ?? spacecraft.id}${expanded ? "" : " (compact)"}`}
        style={{
          position: "relative",
          overflow: "hidden",
          // Use glass container style for bold/minimal themes
          ...(useGlassMode
            ? glassContainerStyle
            : {
                backgroundColor: cardBg,
                ...cardGlass,
                ...cardBorderStyle,
              }),
          borderRadius: tokens.borderRadius.lg,
          padding: expanded ? tokens.spacing.md : tokens.spacing.sm,
          fontFamily: tokens.typography.fontFamily.primary,
          color: tokens.colors.text.primary,
          fontSize: expanded
            ? tokens.typography.fontSize.base
            : tokens.typography.fontSize.sm,
          cursor: "pointer",
          transition: `all ${animationDuration}ms ${animationEasing}`,
          boxShadow: expanded
            ? `0 12px 40px rgba(0,0,0,0.4), 0 0 0 1px ${orbitColor}30`
            : "none",
          zIndex: expanded ? 50 : 1,
        }}
      >
        {/* Glass gradient overlay for bold/minimal themes */}
        {useGlassMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}
        {expanded ? (
          // Full expanded content
          <>
            {/* Full expanded header - matches MonitoringIcon layout pattern */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.spacing.sm,
                marginBottom: tokens.spacing.md,
              }}
            >
              {/* Icon with status badge - MonitoringIcon pattern */}
              <HeaderIconWithStatus
                icon="satellite-transmit"
                size={24}
                status={healthStatus}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: tokens.typography.fontSize.md,
                      fontWeight: tokens.typography.fontWeight.semibold,
                      lineHeight: 1.2,
                    }}
                  >
                    {spacecraft.name ?? spacecraft.id}
                  </h3>
                  {isPinned && (
                    <Icon
                      name="lock"
                      size={12}
                      color={tokens.colors.accent.primary}
                    />
                  )}
                </div>
                {spacecraft.id && spacecraft.name && (
                  <span
                    style={{
                      fontSize: tokens.typography.fontSize.xs,
                      color: tokens.colors.text.tertiary,
                    }}
                  >
                    {spacecraft.id}
                  </span>
                )}
              </div>
              <span
                style={{
                  backgroundColor: `${orbitColor}20`,
                  color: orbitColor,
                  padding: `4px 8px`,
                  borderRadius: tokens.borderRadius.sm,
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: tokens.typography.fontWeight.medium,
                  flexShrink: 0,
                }}
              >
                {spacecraft.orbitType ?? "Unknown"}
              </span>
            </div>

            {/* Position Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: tokens.spacing.sm,
                marginBottom: tokens.spacing.md,
              }}
            >
              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.text.tertiary,
                    marginBottom: 2,
                  }}
                >
                  Latitude
                </span>
                <span
                  style={{
                    fontSize: tokens.typography.fontSize.md,
                    fontWeight: tokens.typography.fontWeight.medium,
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: tokens.typography.fontFamily.mono,
                  }}
                >
                  {formatCoordinate(spacecraft.latitude, "lat")}
                </span>
              </div>
              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.text.tertiary,
                    marginBottom: 2,
                  }}
                >
                  Longitude
                </span>
                <span
                  style={{
                    fontSize: tokens.typography.fontSize.md,
                    fontWeight: tokens.typography.fontWeight.medium,
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: tokens.typography.fontFamily.mono,
                  }}
                >
                  {formatCoordinate(spacecraft.longitude, "lon")}
                </span>
              </div>
              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.text.tertiary,
                    marginBottom: 2,
                  }}
                >
                  Altitude
                </span>
                <span
                  style={{
                    fontSize: tokens.typography.fontSize.md,
                    fontWeight: tokens.typography.fontWeight.medium,
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: tokens.typography.fontFamily.mono,
                    color: orbitColor,
                  }}
                >
                  {formatAltitude(spacecraft.altitude)}
                </span>
              </div>
              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.text.tertiary,
                    marginBottom: 2,
                  }}
                >
                  Velocity
                </span>
                <span
                  style={{
                    fontSize: tokens.typography.fontSize.md,
                    fontWeight: tokens.typography.fontWeight.medium,
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: tokens.typography.fontFamily.mono,
                  }}
                >
                  {formatVelocity(spacecraft.velocity)}
                </span>
              </div>
            </div>

            {/* Timestamp */}
            {spacecraft.timestamp && (
              <time
                dateTime={spacecraft.timestamp}
                style={{
                  display: "block",
                  fontSize: tokens.typography.fontSize.xs,
                  color: tokens.colors.text.tertiary,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatTime(spacecraft.timestamp)}
              </time>
            )}
          </>
        ) : (
          // Compact content
          renderCompactContent()
        )}
      </article>
    );
  }

  return (
    <article
      className={classNames(
        "zendir-spacecraft-card",
        compact ? "expanded" : "",
        className,
      )}
      onClick={compact || onClick ? combinedClickHandler : undefined}
      onKeyDown={onClick ? handleKeyDown : compactHandlers.onKeyDown}
      tabIndex={onClick || compact ? 0 : undefined}
      role={compact ? compactHandlers.role : "article"}
      aria-expanded={compact ? compactHandlers["aria-expanded"] : undefined}
      aria-label={`Spacecraft: ${spacecraft.name ?? spacecraft.id}`}
      style={{
        position: "relative",
        overflow: "hidden",
        ...(useGlassMode
          ? glassContainerStyle
          : {
              backgroundColor: cardBg,
              ...cardGlass,
              ...cardBorderStyle,
            }),
        borderRadius: tokens.borderRadius.lg,
        padding: tokens.spacing.md,
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
        cursor: onClick || compact ? "pointer" : "default",
        transition: `all ${transitionDuration} ease-out`,
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (compact) compactHandlers.onMouseEnter();
        if (onClick) {
          e.currentTarget.style.borderColor = tokens.colors.border.focus;
          e.currentTarget.style.boxShadow = tokens.shadows.md;
          e.currentTarget.style.transform = prefersReducedMotion
            ? "none"
            : "translateY(-2px)";
        }
      }}
      onMouseLeave={(e) => {
        if (compact) compactHandlers.onMouseLeave();
        if (onClick) {
          e.currentTarget.style.borderColor = tokens.colors.border.muted;
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "none";
        }
      }}
      onFocus={(e) => {
        e.currentTarget.style.outline = `2px solid ${tokens.colors.border.focus}`;
        e.currentTarget.style.outlineOffset = "2px";
      }}
      onBlur={(e) => {
        e.currentTarget.style.outline = "none";
      }}
    >
      {/* Glass gradient overlay for bold/minimal themes */}
      {useGlassMode && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Unified Card Header - handles all theme variants */}
      <CardHeader
        icon="satellite-transmit"
        title={spacecraft.name ?? spacecraft.id ?? "Unknown"}
        subtitle={spacecraft.id && spacecraft.name ? spacecraft.id : undefined}
        status={healthStatus}
        badge={spacecraft.orbitType ?? "Unknown"}
        badgeColor={orbitColor}
        isPinned={compact && isPinned}
      />

      {/* Position Data Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: tokens.spacing.sm,
        }}
      >
        <DataCell
          label="Latitude"
          value={formatCoordinate(spacecraft.latitude, "lat")}
          tokens={tokens}
          highlightColor={accentTextColor}
        />
        <DataCell
          label="Longitude"
          value={formatCoordinate(spacecraft.longitude, "lon")}
          tokens={tokens}
          highlightColor={accentTextColor}
        />
        <DataCell
          label="Altitude"
          value={formatAltitude(spacecraft.altitude)}
          highlight
          tokens={tokens}
          highlightColor={accentTextColor}
        />
        <DataCell
          label="Velocity"
          value={formatVelocity(spacecraft.velocity)}
          tokens={tokens}
          highlightColor={accentTextColor}
        />
      </div>

      {/* Orbit Info (Optional) */}
      {showOrbit && spacecraft.inclination !== undefined && (
        <div
          style={{
            marginTop: tokens.spacing.md,
            paddingTop: tokens.spacing.md,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
          }}
        >
          <DataCell
            label="Inclination"
            value={`${safeNumber(spacecraft.inclination, 1)}°`}
            inline
            tokens={tokens}
          />
        </div>
      )}

      {/* Telemetry Summary (Optional) */}
      {showTelemetry && telemetry && (
        <div
          style={{
            marginTop: tokens.spacing.md,
            paddingTop: tokens.spacing.md,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
          }}
        >
          {/* Health Status */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              marginBottom: tokens.spacing.sm,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: statusColor,
                boxShadow: `0 0 8px ${statusColor}60`,
              }}
            />
            <span
              style={{
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: statusColor,
                textTransform: "capitalize",
              }}
            >
              {telemetry.health?.status ?? "Unknown"}
            </span>
          </div>

          {/* Battery Progress */}
          {telemetry.power?.batteryLevel !== undefined && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                  fontSize: tokens.typography.fontSize.xs,
                }}
              >
                <span style={{ color: tokens.colors.text.tertiary }}>
                  Battery
                </span>
                <span
                  style={{
                    fontFamily: tokens.typography.fontFamily.mono,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatPercentage(telemetry.power.batteryLevel, false, 0)}
                </span>
              </div>
              <div
                style={{
                  height: 4,
                  backgroundColor: tokens.colors.border.muted,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  role="progressbar"
                  aria-valuenow={telemetry.power.batteryLevel}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Battery level"
                  style={{
                    height: "100%",
                    width: `${Math.min(100, Math.max(0, telemetry.power.batteryLevel))}%`,
                    backgroundColor:
                      telemetry.power.batteryLevel > 30
                        ? tokens.colors.status.normal
                        : tokens.colors.status.critical,
                    borderRadius: 2,
                    transition: `width ${transitionDuration} ease-out`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Timestamp Footer */}
      {spacecraft.timestamp && (
        <footer
          style={{
            marginTop: tokens.spacing.md,
            paddingTop: tokens.spacing.sm,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.tertiary,
            textAlign: "right",
          }}
        >
          <time dateTime={spacecraft.timestamp}>
            {formatTime(spacecraft.timestamp)} UTC
          </time>
        </footer>
      )}
    </article>
  );
});

// ============================================================================
// Helper Components
// ============================================================================

interface DataCellProps {
  label: string;
  value: string;
  highlight?: boolean;
  inline?: boolean;
  tokens: ReturnType<typeof useTheme>["tokens"];
  /** WCAG AA safe highlight color (computed via safeAccentText) */
  highlightColor?: string;
}

const DataCell = memo(function DataCell({
  label,
  value,
  highlight = false,
  inline = false,
  tokens,
  highlightColor,
}: DataCellProps): React.ReactElement {
  if (inline) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.secondary, // WCAG AA compliant
            fontWeight: tokens.typography.fontWeight.medium,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: tokens.typography.fontSize.sm,
            fontFamily: tokens.typography.fontFamily.mono,
            fontVariantNumeric: "tabular-nums",
            color: tokens.colors.text.primary,
          }}
        >
          {value}
        </span>
      </div>
    );
  }

  return (
    <div>
      <span
        style={{
          display: "block",
          fontSize: tokens.typography.fontSize.xs,
          color: tokens.colors.text.secondary, // WCAG AA compliant
          fontWeight: tokens.typography.fontWeight.medium,
          marginBottom: 2,
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: "block",
          fontSize: tokens.typography.fontSize.sm,
          fontFamily: tokens.typography.fontFamily.mono,
          fontVariantNumeric: "tabular-nums",
          color: highlight
            ? (highlightColor ?? safeAccentText(tokens.colors.accent.primary))
            : tokens.colors.text.primary,
          fontWeight: tokens.typography.fontWeight.medium,
        }}
      >
        {value}
      </span>
    </div>
  );
});
